import i18n from 'i18next';
import React, {useEffect, useMemo, useState} from 'react';
import {
	ComponentUtils,
	DetailActions,
	DetailActionType,
	get, notification,
	OrganizationSelection,
	RenderUtils,
	RoleSelection,
	SwAction,
	SwDetail,
	SwForm,
	SwList,
	SwPagination,
	SwProgressBar,
	UniversalCodebookSelection
} from 'simplifyworks-core-ui';
import {connect} from 'react-redux';
import {getEavFields} from '../../utils/eav';
import {FOEDUS_ROLE} from '../../domain/constants';
import {bindActionCreators} from 'redux';
import {RegisteredDevicesList} from 'simplifyworks-push-ui';
import {TbCheck, TbX} from 'react-icons/tb';
import {FaUser} from 'react-icons/fa/';
import {recreateFoedusPerson} from '../../rest/index.js';

const PersonDetail = (props) => {
	const isNew = props.params.id === 'new';

	// Definicie eav poli
	const [personFieldsDefinitions, setPersonFieldsDefinitions] = useState(null);
	const [isReadOnly, setIsReadOnly] = useState(false);

	useEffect(() => {
		get('/api/core/field-definitions/CorePerson',
			(result) => {
				setPersonFieldsDefinitions(result);
			}
		);
	}, []);

	// Setne sa len pokial formular nebol este upraveny
	useEffect(() => {
		if (props?.isReadOnly !== null) {
			setIsReadOnly(props?.isReadOnly);
		}
	}, [props?.isReadOnly]);

	const newCallback = (type, id) => {
		if (type === DetailActionType.DETAIL_CREATE_SUCCESS && id) {
			props.router.push('/contacts/people/' + id);
		}

	};

	const organizationFixedFilter = useMemo(() => {
		const getFilter = (types, id) => ({
			organizationTypes: types ? [types] : [],
			parentId: id
		});

		if (props?.loggedUserPrimaryFoedusRole === FOEDUS_ROLE.COUNTRY_MANAGER) {
			return getFilter(props?.nationalBodyCoreCodebook, props?.loggedUserCountry?.id);
		}

		if (props?.loggedUserPrimaryFoedusRole === FOEDUS_ROLE.ADMIN) {
			const roleName = props?.personDetailFormData?.role?.name;
			if (roleName === FOEDUS_ROLE.COORDINATOR) {
				return getFilter(props?.nationalBodyCoreCodebook, props?.loggedUserCountry?.id);
			}
			if (roleName === FOEDUS_ROLE.COUNTRY_MANAGER) {
				return getFilter(props?.countryCoreCodebook);
			}
		}
	}, [props?.loggedUserPrimaryFoedusRole, props?.personDetailFormData]);

	const organizationEntityField = useMemo(() => {
		const roleName = props?.personDetailFormData?.role?.name;
		const commonProps = {
			property: 'organization',
			size: 6,
			writingEnabled: true,
			preSelect: true,
			labelKey: 'foedus-organization',
			disabled: !props?.personDetailFormData?.role,
			render: OrganizationSelection.renderText
		};

		const renderField = (property, labelKey) => (
			<SwForm.Entity {...commonProps} property={property} labelKey={labelKey} validations='required'>
				{OrganizationSelection.renderSelection({
					name: 'foedus-organization',
					fixedFilter: organizationFixedFilter
				})}
			</SwForm.Entity>
		);

		if (props?.loggedUserPrimaryFoedusRole === FOEDUS_ROLE.COUNTRY_MANAGER) {
			if (isNew || roleName === FOEDUS_ROLE.COORDINATOR) {
				return renderField('organization', 'foedus-organization');
			}
			if (roleName === FOEDUS_ROLE.COUNTRY_MANAGER) {
				return renderField('country', 'foedus-country');
			}
		}

		if (props?.loggedUserPrimaryFoedusRole === FOEDUS_ROLE.ADMIN) {
			if (roleName === FOEDUS_ROLE.COORDINATOR) {
				return renderField('organization', 'foedus-organization');
			}
			if (roleName === FOEDUS_ROLE.COUNTRY_MANAGER) {
				return renderField('country', 'foedus-country');
			}
		}

		return (
			<SwForm.Entity {...commonProps} disabled>
				{OrganizationSelection.renderSelection({
					name: 'foedus-person-organization',
					fixedFilter: organizationFixedFilter
				})}
			</SwForm.Entity>
		);
	}, [props?.personDetailFormData?.role, props?.loggedUserRoles, isNew, organizationFixedFilter]);

	if (props?.isMyAccount && !props?.loggedUserFoedusPerson?.id) {
		return <SwProgressBar centered />;
	}

	return <>
		<SwDetail
			rest='/api/foedus/contacts/person'
			restId={props?.formId}
			tabName={props?.isMyAccount ? 'base-info' : props?.params?.tabName}
			newCallback={newCallback}
			disabled={isReadOnly && !isNew}
		>
			{props?.isSifyAdmin ? (
				<SwAction
					getOptions={() => ({
						onClick: () => {
							notification.showMessage({
								level: 'info',
								title: i18n.t('foedusMsg-recreateInProgress'),
								message: <SwProgressBar centered />
							});

							const personId = props?.formId;

							recreateFoedusPerson(
								personId,
								(res) => {
									notification.showLocalizedMessage({
										level: 'success',
										titleKey: 'foedusMsg-recreateSuccess'
									});

									notification.removeMessage({
										level: 'info',
										title: i18n.t('foedusMsg-recreateInProgress'),
										message: <SwProgressBar centered />
									});
								},
								(err) => {
									console.error(err);
									notification.showLocalizedMessage({
										level: 'error',
										titleKey: 'foedusMsg-recreateError'
									});

									notification.removeMessage({
										level: 'info',
										title: i18n.t('foedusMsg-recreateInProgress'),
										message: <SwProgressBar centered />
									});
								}
							);
						},
						labelKey: 'foedus-recreateUser',
						icon: FaUser
					})}
				/>
			) : null}



			<SwPagination listName={'foedus-contacts-people-list'} />

			<SwForm
				rest='base-info'
				labelKey='basic-info'
				new={isNew}
				tabName='base-info'
				initialValue={{
					user: {
						newPassword: null
					},
					person: {
						sex: 'N'
					}}}>
				<SwForm.Section type='panel' labelKey='foedus-contact'>
					<SwForm.Section>
						<SwForm.String
							size={2}
							property='person.titleBefore'
							labelKey='core-person-titleBefore'
						/>
						<SwForm.String
							size={3}
							property='person.firstname'
							labelKey='core-person-firstname'
							validations="required;lengthRange(0,100)"
						/>
						<SwForm.String
							size={3}
							property='person.surname'
							labelKey='core-person-surname'
							validations="required;lengthRange(0,100)"
						/>
						<SwForm.String
							size={2}
							property='person.titleAfter'
							labelKey='core-person-titleAfter'
						/>
						<SwForm.Enum
							size={2}
							property='person.sex'
							labelKey='foedus-gender'
							showNull={false}
							options={
								{
									enum: 'core-person-sex',
									values: ['M', 'F', 'N', 'NA']
								}
							}
							validations="required"
						/>

						<SwForm.String
							size={6}
							property='person.phone'
							labelKey='foedus-recovery-phone'
							helpKey='foedus-recovery-phone-help'
							hidden={isReadOnly && !isNew}
							customValidation={(value) => {
								const v = String(value ?? '').trim();

								if (!v) {
									return {
										name: 'validation-required'
									};
								}

								if (/[^+\d]/.test(v)) {
									return {
										name: 'foedus-recovery-phone-validation-noSeparators',
										args: { example: '+421905123456' }
									};
								}

								if (!/^\+[1-9]\d{1,14}$/.test(v)) {
									return {
										name: 'foedus-recovery-phone-validation-error',
										args: { example: '+421905123456' }
									};
								}

								return null;
							}}
						/>
					</SwForm.Section>

					<SwForm.Section>
						<SwForm.String
							size={6}
							property='user.username'
							labelKey='foedus-authentication-email'
							validations="required;email"
							disabled={!isNew}
						/>

						{RoleSelection.renderSimpleSelection({
							labelKey: 'core-role',
							property: 'role',
							fixedFilter: {
								roleNames: props?.roleNamesFilter
							},
							render: rec => rec?.description ?? rec?.descriptionEn,
							renderOption: rec => rec?.description ?? rec?.descriptionEn,
							preSelect: true,
							validations: 'required',
							disabled: props?.isCurrentUserCoordinator || props?.isMyAccount
						})}

						<SwForm.String
							size={6}
							property='user.repeatUsername'
							labelKey='foedus-repeat-authentication-email'
							customValidation={value => {
								if (!value || value === '' || props?.personDetailFormData?.user?.username !== value) {
									return {
										name: 'foedus-repeat-authentication-email-error'
									};
								}
							}}
							hidden={!isNew}
						/>

						<SwForm.Boolean
							size={6}
							property='user.active'
							labelKey='core-user-active'
							disabled={props?.isCurrentUserCoordinator}
						/>

						{organizationEntityField}
					</SwForm.Section>

					<SwForm.Section>
						<SwForm.String
							size={12}
							property='person.note'
							labelKey='core-person-note'
							type='area'
						/>
					</SwForm.Section>

					<SwForm.Section>
						{getEavFields(personFieldsDefinitions, 'person')}
					</SwForm.Section>

					<SwForm.Section>
						<SwForm.Embeddables size={12} property='phones' labelKey='foedus-phones'>
							<SwForm.EmbeddablesList
								name='phones'
								draggable
								defaultOrdering={[{property: 'rank', order: 'asc'}]}
							>

								<SwForm.EmbeddablesListCreateButton
									labelKey='core-person-phone'
									initialValue={{
										primary: false
									}}
								/>

								<SwList.Table>
									<SwList.Custom
										property='phone'
										labelKey='core-person-phone'
										pathProperty='country'
										render={resource => resource?.country?.fieldValuesMap?.phonePrefix +  ' ' + resource?.phone}
									/>
									<SwList.Boolean
										property='primary'
										labelKey='foedus-primary'
									/>
								</SwList.Table>
							</SwForm.EmbeddablesList>

							<SwForm.EmbeddableForm
								name='phone'
								labelKey='foedus-phone'
							>
								<SwForm.Section>
									<SwForm.Entity
										property='country'
										labelKey='foedus-country'
										validations="required"
										size={5}
										writingEnabled
										preSelect
										render={resource =>
											resource?.fieldValuesMap?.phonePrefix ?
												RenderUtils.renderMultilangProperty(resource, 'name') + ' (' + resource?.fieldValuesMap?.phonePrefix + ')'
												: RenderUtils.renderMultilangProperty(resource, 'name')
										}
									>
										{UniversalCodebookSelection.renderSelection({
											name: 'foedus-country',
											codebookType: 'CoreCountryCodebook'
										})}
									</SwForm.Entity>
									<SwForm.String
										size={7}
										property='phone'
										labelKey='core-person-phone'
										validations="lengthRange(0,50);required"
									/>
									<SwForm.Boolean
										property='primary'
										labelKey='foedus-primary'
										helpKey='foedus-primary-help'
										size={6}
										showNull={false}
									/>
								</SwForm.Section>
							</SwForm.EmbeddableForm>
						</SwForm.Embeddables>
					</SwForm.Section>

					<SwForm.Section>
						<SwForm.Embeddables size={12} property='emails' labelKey='foedus-notification-emails'>
							<SwForm.EmbeddablesList
								name='emails'
								draggable
								defaultOrdering={[{property: 'rank', order: 'asc'}]}
							>

								<SwForm.EmbeddablesListCreateButton
									labelKey='core-person-email'
								/>

								<SwList.Table>
									<SwList.String
										property='email'
										validations="required;email"
										labelKey='core-person-email'
										pathProperty='email'
									/>
									<SwList.Boolean
										property='sendNotifications'
										labelKey='foedus-sendNotifications'
									/>
								</SwList.Table>
							</SwForm.EmbeddablesList>

							<SwForm.EmbeddableForm
								name='email'
								labelKey='foedus-email'
							>
								<SwForm.Section>
									<SwForm.String
										size={12}
										property='email'
										labelKey='core-person-email'
									/>
									<SwForm.Boolean
										size={12}
										property='sendNotifications'
										labelKey='foedus-sendNotifications'
									/>
								</SwForm.Section>
							</SwForm.EmbeddableForm>
						</SwForm.Embeddables>
					</SwForm.Section>

					<SwForm.Section>
						<SwForm.Embeddables size={12} property='faxes' labelKey='foedus-faxes'>
							<SwForm.EmbeddablesList
								name='faxes'
								draggable
								defaultOrdering={[{property: 'rank', order: 'asc'}]}
							>

								<SwForm.EmbeddablesListCreateButton
									labelKey='foedus-fax'
								/>

								<SwList.Table>
									<SwList.Custom
										property='fax'
										labelKey='foedus-fax-number'
										pathProperty='fax'
										render={resource => resource?.country?.fieldValuesMap?.phonePrefix + ' ' + resource?.fax}
									/>
								</SwList.Table>
							</SwForm.EmbeddablesList>

							<SwForm.EmbeddableForm
								name='faxes'
								labelKey='foedus-fax'
							>
								<SwForm.Section>
									<SwForm.Entity
										size={5}
										property='country'
										labelKey='foedus-country'
										validations="required"
										render={resource => RenderUtils.renderMultilangProperty(resource, 'name') + ' (' + resource?.fieldValuesMap?.phonePrefix + ')'}
										writingEnabled
										preSelect
									>
										{UniversalCodebookSelection.renderSelection({
											name: 'foedus-country',
											codebookType: 'CoreCountryCodebook'
										})}
									</SwForm.Entity>
									<SwForm.String
										size={7}
										property='fax'
										labelKey='foedus-fax-number'
										validations="lengthRange(7,15);required"
									/>
								</SwForm.Section>
							</SwForm.EmbeddableForm>
						</SwForm.Embeddables>
					</SwForm.Section>

				</SwForm.Section>

				{!isNew && (
					<SwForm.Section type='panel' labelKey='foedus-contacts-registered-devices'>

						<RegisteredDevicesList
							name={'registered-devices-' + props?.personDetailFormData?.user?.username}
							fixedFilter={{
								username: props?.personDetailFormData?.user?.username
							}}
							columns={[
								// Overrides for default columns
								{
									property: 'created',
									labelKey: 'push-registration-date',
									component: SwList.Datetime,
									userPattern: 'D.M.YYYY, HH:ss',
									index: 5
								},
								{
									property: 'disabled',
									labelKey: 'push-status',
									filterable: true,
									width: '100px',
									component: SwList.Custom,
									render: (resource) => (
										<div className="d-flex align-items-center justify-content-center w-100 h-100">
											{resource?.disabled ? <TbX color={'#EC3D3D'}/> :
												<TbCheck size={24} color={'#28a745'}/>}
										</div>
									),
									index: 6
								}
							]}
						/>
					</SwForm.Section>
				)}

			</SwForm>
		</SwDetail>
	</>;
};

const mapStateToProps = (state, ownProps) => {
	const isMyAccount = state?.router?.location.pathname.endsWith('/contacts/my-account');
	const formId = isMyAccount ? state?.foedus?.loggedUserFoedusPerson?.id : ownProps.params.id;
	const personDetail = ComponentUtils.getDetail(state, '/api/foedus/contacts/person/' + formId);
	const personDetailFormData = ComponentUtils.getFormData(state, '/api/foedus/contacts/person/' + formId, 'base-info');

	const getIsReadOnly = () => {
		// Ak je uzivatel na detaile sameho seba
		if ((state?.security?.tokenInfo?.currentUsername === personDetailFormData?.user?.username) || isMyAccount) {
			return false;
		}

		// Foedus admin moze editovat vsetkych
		if (state?.security?.allRoles?.includes(FOEDUS_ROLE.ADMIN)) {
			return false;
		}

		// Ak je country manager tak moze editovat len coordinatorov z jeho krajiny
		if (state?.security?.allRoles?.includes(FOEDUS_ROLE.COUNTRY_MANAGER)) {
			const foundOrgCode = state?.security?.rolesInOrganizations?.find(roleInOrg => roleInOrg?.roleName === FOEDUS_ROLE.COUNTRY_MANAGER)?.organizationCode;
			const roleName = personDetailFormData?.role?.name;

			if (roleName === FOEDUS_ROLE.COORDINATOR) {
				return foundOrgCode !== personDetailFormData?.organization?.organization?.code;
			}

			if (roleName === FOEDUS_ROLE.COUNTRY_MANAGER ) {
				return true;
			}
		}

		return true;
	};

	return {
		organOfferAttachmentTypeCbIds: state?.foedus?.organOfferAttachmentTypeCbIds,
		nationalBodyCoreCodebook: state?.foedus?.nationalBodyCoreCodebook,
		countryCoreCodebook: state?.foedus?.countryCoreCodebook,
		loggedUserPrimaryFoedusRole: state?.foedus?.loggedUserPrimaryFoedusRole,
		roleNamesFilter: state?.foedus?.foedusRoleNamesFilter,
		loggedUserCountry: state?.foedus?.loggedUserCountry,
		loggedUserFoedusPerson: state?.foedus?.loggedUserFoedusPerson,
		loggedUserRoles: state?.security?.allRoles,
		isCurrentUserCoordinator: state?.security?.allRoles?.includes(FOEDUS_ROLE.COORDINATOR)
			&& !state?.security?.allRoles?.includes(FOEDUS_ROLE.COUNTRY_MANAGER)
			&& !state?.security?.allRoles?.includes(FOEDUS_ROLE.ADMIN),
		isReadOnly: !personDetail?.modified ? getIsReadOnly() : null, // Setne sa len pokial formular nebol este upraveny, inak si komponent drzi svoj state
		isSifyAdmin: state?.security?.allRoles?.includes('admin'),
		personDetailFormData,
		isMyAccount,
		formId
	};
};

const mapDispatchToProps = (dispatch) => ({
	actions: {
		detail: bindActionCreators(DetailActions, dispatch)
	}
});

export default {
	title: ({router}) => router?.location?.pathname === '/contacts/my-account'
		? i18n.t('foedus-my-account')
		: i18n.t('foedus-contacts-people-detail'),
	main: connect(mapStateToProps, mapDispatchToProps)(PersonDetail)
};
